/**
 * useMigration Hook
 *
 * Manages isolation migration: validate, start, poll progress.
 * Follows the same polling pattern as useTenantUpgrade.
 */

import { useState, useCallback, useRef } from 'react';
import {
  MigrationValidation,
  MigrationProgress,
  validateMigration,
  startMigration,
  fetchMigrationProgress,
} from '../api/migrationApi';

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

const POLL_INTERVAL = 3000;
const MAX_POLLS = 200; // 10 min max

export interface UseMigrationResult {
  validation: MigrationValidation | null;
  progress: MigrationProgress | null;
  loading: boolean;
  error: string | null;
  validate: (tenantId: string, target: string) => Promise<MigrationValidation | null>;
  start: (tenantId: string, target: string) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

export function useMigration(authFetch: AuthFetch): UseMigrationResult {
  const [validation, setValidation] = useState<MigrationValidation | null>(null);
  const [progress, setProgress] = useState<MigrationProgress | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    setValidation(null);
    setProgress(null);
    setError(null);
    setLoading(false);
    stopPolling();
  }, [stopPolling]);

  const validateFn = useCallback(async (tenantId: string, target: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await validateMigration(authFetch, tenantId, target);
      setValidation(result);
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Validation failed');
      return null;
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  const startFn = useCallback(async (tenantId: string, target: string) => {
    setLoading(true);
    setError(null);
    try {
      await startMigration(authFetch, tenantId, target);

      // Start polling
      let pollCount = 0;
      let notFoundCount = 0;
      stopPolling();
      pollRef.current = setInterval(async () => {
        pollCount++;
        if (pollCount >= MAX_POLLS) {
          stopPolling();
          setError('Migration timed out');
          setLoading(false);
          return;
        }
        try {
          const updated = await fetchMigrationProgress(authFetch, tenantId);
          notFoundCount = 0;
          setProgress(updated);
          if (['completed', 'failed', 'rolled_back'].includes(updated.status)) {
            stopPolling();
            setLoading(false);
          }
        } catch {
          notFoundCount++;
          // If progress 404s repeatedly, the migration may have completed
          // before polling started. Check the status endpoint as fallback.
          if (notFoundCount >= 3) {
            try {
              const { fetchMigrationStatus } = await import('../api/migrationApi');
              const status = await fetchMigrationStatus(authFetch, tenantId);
              if (!status.has_active_migration) {
                // Migration finished before polling could start.
                // Check if isolation actually changed to determine success vs failure.
                const succeeded = status.current_isolation === target;
                setProgress({
                  tenant_id: tenantId,
                  tenant_slug: status.tenant_slug,
                  direction: target,
                  status: succeeded ? 'completed' : 'failed',
                  current_step: succeeded ? 1 : 0,
                  total_steps: 1,
                  progress_pct: succeeded ? 100 : 0,
                  message: succeeded
                    ? `Migration completed. Isolation: ${status.current_isolation}`
                    : `Migration failed. Isolation unchanged: ${status.current_isolation}`,
                  started_at: null,
                  completed_at: new Date().toISOString(),
                  error: succeeded ? null : 'Migration failed — check logs for details',
                  rows_migrated: 0,
                  tables_migrated: 0,
                });
                stopPolling();
                setLoading(false);
              }
            } catch {
              // Fallback also failed, keep polling
            }
          }
        }
      }, POLL_INTERVAL);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start migration');
      setLoading(false);
    }
  }, [authFetch, stopPolling]);

  return {
    validation,
    progress,
    loading,
    error,
    validate: validateFn,
    start: startFn,
    clearError,
    reset,
  };
}
